import { useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ContactCard, type CardSlot } from "@/components/interview/PersonPicker";
import { FullTextCell, FullTextPanel } from "@/components/interview/FullText";
import { saveSettled, useSaveFailure } from "@/components/interview/saveFailure";
import { parseAddress } from "@shared/address";
import { CONTACTS_FILE_NAMES, contactsCsv, contactsVcf } from "@shared/contacts-export";
import { deliveryFailureMessage, type DeliveryReason } from "@shared/upload-delivery";
import { HOME_BOARD, fill } from "./planBoard";
import { PlanSearch } from "./PlanSearch";
import { FilterChips } from "./FilterChips";
import { SortToggle, type SortWay } from "./SortToggle";
import { CopyButton } from "./CopyButton";
import { addressCell, addressLine, cardNoteName, chipsFor, downloadScope, saveTextFile, shownContacts, toExport, type ChipId, type ContactEntry } from "./contactsThings";

/**
 * s237 (ACROSS-YOUR-PLAN-1 stage 1, PR C; D-237.34, D-237.37) — CONTACTS, the planner's view (board 12 :1255–1379).
 * Each person or company once, with every part they play. Search, one row of filter chips, sort by "Who"; email and
 * phone copy with one click. Clicking a name opens the interview's own contact card under the row (`ContactCard`, never
 * a redrawn copy): one record, so a change shows everywhere the person appears. Each role links to the built screen that
 * chose it ("← Contacts"); a role whose screen is not built yet is plain words. The words are board 12's
 * (`HOME_BOARD.contacts`, RULE 137).
 */
const C = HOME_BOARD.contacts;
/** Board 12's grid: the tick box · Who · What they are to you · Email · Phone. */
// s237 PR E (D-237.68-2, canvas v477): the name narrower, roles and email wider, Phone 156px, gap 12px. PR F (D-237.72): the
// 32px tick column at the start.
const GRID = "32px minmax(0, 0.95fr) minmax(0, 1.55fr) minmax(0, 1.55fr) 156px";
export const contactsKey = (clientId: string) => ["/api/clients", clientId, "contacts-page"] as const;

export function ContactsPage({ clientId, onOpen, executor }: {
  clientId: string;
  onOpen: (screenKey: string) => void;
  /** s237 PR D part 2 (D-236.50-25(b)): the executor's view — the planner's first name for the line and the blanks. The
   *  executor has no copy of the interview's screens, so a role is words, never a link; the card's Done patches the same
   *  person through the same route, which records the executor's change (D-237.35). */
  executor?: { planner: string };
}) {
  // D-237.68-4 / D-237.74: a blank in the LIST reads "—" for everyone. Inside the card the executor's empty boxes read
  // board 12's "{planner} did not say", so they know to ask (rule 141).
  const cardEmpty = executor ? fill(HOME_BOARD.where.exNotSaid, { planner: executor.planner }) : undefined;
  const { data, isSuccess } = useQuery<{ contacts: ContactEntry[] }>({
    queryKey: contactsKey(clientId),
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/contacts-page`)).json(),
  });
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState<ChipId>("all");
  const [sort, setSort] = useState<SortWay>("az");
  const [openId, setOpenId] = useState<string | null>(null);
  const [rolesOpen, setRolesOpen] = useState<Record<string, boolean>>({});
  const [full, setFull] = useState<Record<string, boolean>>({});
  // D-237.72: the tick boxes, by person id. A ticked person stays ticked when a filter hides them.
  const [ticked, setTicked] = useState<Record<string, boolean>>({});

  const onSaveFailed = useSaveFailure();
  // ONE lock on the card's Done (the single-flight rule): the person is patched once.
  const saveFlight = useSingleFlight();
  const saveMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => apiRequest("PATCH", `/api/clients/${clientId}/contacts/${id}`, body),
    onError: onSaveFailed,
  });
  const saveCard = (c: ContactEntry, changes: Record<string, string>) => {
    const body = contactPatch(changes);
    if (Object.keys(body).length === 0 || !c.id) return Promise.resolve(true);
    return saveFlight.run(() => saveSettled(saveMutation.mutateAsync({ id: c.id!, body }))).then((ok) => {
      if (ok) {
        void queryClient.invalidateQueries({ queryKey: contactsKey(clientId) });
        void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "contacts"] });
        void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
      }
      return !!ok;
    });
  };

  const entries = (data?.contacts ?? []).map((e) => (executor ? { ...e, roles: e.roles.map((r) => ({ ...r, target: null })) } : e));
  const chips = chipsFor(entries);
  const rows = shownContacts(entries, { chip, query, sort });
  const shownIds = rows.flatMap((r) => (r.id ? [r.id] : []));
  const allTicked = shownIds.length > 0 && shownIds.every((id) => ticked[id]);
  const tickAll = () => setTicked(allTicked ? {} : Object.fromEntries(shownIds.map((id) => [id, true])));
  const scope = downloadScope(entries, rows, ticked, sort);
  const listPhone = useSendToPhone(clientId);

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-3.5 px-4 pb-16 pt-6 sm:px-8" data-testid="contacts-page">
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] text-warm-muted" data-testid="contacts-from">{executor ? C.exFrom : C.from}</span>
        <h1 className="font-serif text-[34px] font-normal leading-tight text-warm-charcoal" data-testid="contacts-title">{C.title}</h1>
        <p className="text-[14.5px] leading-normal text-warm-charcoal" data-testid="contacts-note">{executor ? fill(C.exNote, { planner: executor.planner }) : C.note}</p>
      </div>

      {/* Board 12: the search, then the Download button on the same line. */}
      <div className="flex items-center gap-2.5">
        <div className="min-w-0 flex-1"><PlanSearch value={query} onChange={setQuery} placeholder={C.search} testId="contacts-search" /></div>
        <DownloadMenu list={scope.list} ticked={scope.ticked} onPhone={() => void listPhone.send(scope.list.map((e) => e.id!))} onOpen={listPhone.reset} />
      </div>
      {/* "Contacts to my phone": Sent only when it was sent; otherwise the honest failure and the link (s168). */}
      {listPhone.result?.sent && (
        <span className="self-end text-[13.5px] text-sage-800" role="status" data-testid="contacts-download-sent">{C.toPhone.sent}</span>
      )}
      {listPhone.result && !listPhone.result.sent && (
        <div className="self-end max-w-[520px]"><SendFailure result={listPhone.result} testId="contacts-download-failed" /></div>
      )}
      {chips.length > 1 && <FilterChips chips={chips} value={chip} onChange={setChip} testId="contacts-chips" />}

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-[12px] border border-warm-stone bg-warm-white" data-testid="contacts-table">
          <div className="grid items-center gap-3 bg-warm-stone-light px-5 py-1 text-[14.5px] font-semibold text-warm-charcoal" style={{ gridTemplateColumns: GRID }}>
            <TickBox on={allTicked} onFlip={tickAll} title={C.tick.all} testId="contacts-tick-all" />
            <SortToggle label={C.heads[0]} way={sort} onFlip={() => setSort((s) => (s === "az" ? "za" : "az"))} words={C.sort} title={C.sort.title} testId="contacts-sort" />
            <span>{C.heads[1]}</span><span>{C.heads[2]}</span><span>{C.heads[3]}</span>
          </div>
          {rows.map((c, i) => {
            const key = c.id ?? `spouse-${i}`;
            const open = !!c.id && openId === c.id;
            const showAll = !!rolesOpen[key];
            const company = c.kind === "organisation";
            return (
              <div key={key} className={`border-t border-warm-stone ${open ? "bg-sage-50" : ""}`} data-testid={`contacts-row-${key}`}>
                <div className="grid min-h-12 items-center gap-3 px-5 py-0.5" style={{ gridTemplateColumns: GRID }}>
                  {c.id ? <TickBox on={!!ticked[c.id]} onFlip={() => setTicked((t) => { const n = { ...t }; if (n[c.id!]) delete n[c.id!]; else n[c.id!] = true; return n; })}
                    title={C.tick.one} testId="contacts-tick" /> : <span />}
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Icon company={company} />
                    {c.id ? (
                      <button type="button" onClick={() => setOpenId(open ? null : c.id)} title={C.open.title} aria-expanded={open} data-contact-trigger=""
                        className="flex min-h-11 min-w-0 flex-col justify-center text-left text-[16px] text-warm-charcoal" data-testid="contacts-name">
                        {/* D-237.68-1: no "change" word on this page — the underline says the name opens the card. */}
                        <span className="line-clamp-3 underline decoration-warm-stone underline-offset-[3px] [overflow-wrap:anywhere]">{c.name}</span>
                        {/* D-237.69-2: the line under a name is a FIRM only, never a relationship the roles column says. */}
                        {c.firm && <span className="line-clamp-1 text-[13.5px] text-warm-muted" data-testid="contacts-firm">{c.firm}</span>}
                      </button>
                    ) : (
                      <span className="min-w-0 text-[16px] text-warm-charcoal" data-testid="contacts-name">{c.name}</span>
                    )}
                  </div>
                  <RolesCell roles={c.roles} open={showAll} onToggle={() => setRolesOpen((o) => ({ ...o, [key]: !o[key] }))} onOpen={onOpen} />
                  <div className="flex min-w-0 items-center gap-1">
                    {c.email ? (
                      <>
                        <FullTextCell text={c.email} open={!!full[`${key}:email`]} onToggle={() => setFull((f) => ({ ...f, [`${key}:email`]: !f[`${key}:email`] }))}
                          textClassName="line-clamp-1 break-all text-[15px] text-warm-charcoal" title={HOME_BOARD.where.showAll} testId="contacts-email" />
                        <CopyButton text={c.email} title={C.copy.title} testId="contacts-copy-email" />
                      </>
                    ) : <span className="text-[15px] text-warm-muted" data-testid="contacts-no-email">{C.blank}</span>}
                  </div>
                  <div className="flex min-w-0 items-center gap-1">
                    {!c.phone && <span className="text-[15px] text-warm-muted" data-testid="contacts-no-phone">{C.blank}</span>}
                    {c.phone && (
                      <>
                        {/* A long number in the 156px column: one line with the expand control (rule 133(a)), as the email. */}
                        <FullTextCell text={c.phone} open={!!full[`${key}:phone`]} onToggle={() => setFull((f) => ({ ...f, [`${key}:phone`]: !f[`${key}:phone`] }))}
                          textClassName="line-clamp-1 break-all text-[15px] text-warm-charcoal" title={HOME_BOARD.where.showAll} testId="contacts-phone" />
                        <CopyButton text={c.phone} title={C.copy.title} testId="contacts-copy-phone" />
                      </>
                    )}
                  </div>
                </div>
                {showAll && (
                  <div className="flex flex-wrap items-center gap-x-1 px-5 pb-2" data-testid="contacts-roles-all">
                    {c.roles.map((role, ri) => <Role key={ri} role={role} onOpen={onOpen} />)}
                  </div>
                )}
                {full[`${key}:email`] && c.email && <div className="px-5"><FullTextPanel label={`${C.heads[2]}:`} text={c.email} testId="contacts-email" /></div>}
                {full[`${key}:phone`] && c.phone && <div className="px-5"><FullTextPanel label={`${C.heads[3]}:`} text={c.phone} testId="contacts-phone" /></div>}
                {open && <ContactCardUnderRow c={c} clientId={clientId} emptyWord={cardEmpty} onSave={(ch) => saveCard(c, ch)} onClose={() => setOpenId(null)} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The card's changes (by its slot keys) as the contacts PATCH body. The address is the person's own, saved through the
 *  one person writer (Reade, s237 PR C); an emptied address clears it. Only what changed is sent. Pure. */
export function contactPatch(changes: Record<string, string>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (changes.first !== undefined) body.firstName = changes.first;
  if (changes.last !== undefined) body.surname = changes.last;
  if (changes.name !== undefined) body.surname = changes.name;
  if (changes.phone !== undefined) body.phone = changes.phone;
  if (changes.email !== undefined) body.email = changes.email;
  if (changes.address !== undefined) body.address = parseAddress(changes.address) ?? null;
  return body;
}

/** The interview's own contact card, under the row (board 12: 760px), editing the ONE person record. */
function ContactCardUnderRow({ c, clientId, emptyWord, onSave, onClose }: {
  c: ContactEntry; clientId: string; emptyWord: string | undefined; onSave: (changes: Record<string, string>) => Promise<boolean>; onClose: () => void;
}) {
  const company = c.kind === "organisation";
  const slot: CardSlot = company
    ? { label: C.card.company.name, name: { kind: "text", key: "name" }, phone: "phone", email: "email", address: "address", org: true }
    : { label: C.card.person.name, name: { kind: "nameParts", first: "first", last: "last" }, phone: "phone", email: "email", address: "address" };
  const values = { name: c.name, first: c.firstName ?? (c.lastName ? "" : c.name), last: c.lastName ?? "", phone: c.phone ?? "", email: c.email ?? "", address: addressCell(c) };
  return (
    <div className="flex flex-col gap-2 px-5 pb-4" data-testid="contacts-card">
      <div className="max-w-[760px]">
        {/* D-237.68-3: the address is a box in the card for EVERYONE (open even when empty), saved to the person. The card
            draws a box only for a key it has an id for — PR C passed none, so no address box ever drew (F1, measured). */}
        <ContactCard title={fill(C.card.title, { name: c.name })} slots={[slot]} values={values} fieldIdByKey={CARD_FIELD_IDS} clientId={clientId}
          onSave={(changes) => onSave(changes).then((ok) => { if (ok) onClose(); return ok; })} onClose={onClose} linkedPerson interviewLook
          addressOpenWhenEmpty addressAsLine addressPlaceholder={C.card.addressPh} emptyWord={emptyWord} testId="contacts-card-box"
          headerNote={<span data-testid="contacts-card-note">{fill(C.card.note, { name: cardNoteName(c) })}</span>}
          extra={company ? <AskForBox names={c.askFor ?? []} /> : undefined}
          footerAction={c.id ? <TextToPhone clientId={clientId} personId={c.id} /> : undefined} />
      </div>
      {addressLine(c) && (
        <div className="flex max-w-[760px] items-center gap-2 text-[14px]">
          <span className="text-warm-muted">{C.copy.address}</span>
          <CopyButton text={addressLine(c)} title={C.copy.address} testId="contacts-copy-address" />
        </div>
      )}
    </div>
  );
}

/** A tick box (D-237.72): the header's ticks or clears everyone shown; a row's ticks that person. 44px to touch, 18px drawn. */
function TickBox({ on, onFlip, title, testId }: { on: boolean; onFlip: () => void; title: string; testId: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={on} onClick={onFlip} title={title} aria-label={title}
      className="flex h-11 w-8 items-center justify-center" data-testid={testId}>
      <span className={`flex h-[18px] w-[18px] items-center justify-center rounded-[4px] border-[1.5px] ${on ? "border-sage-600 bg-sage-600" : "border-warm-taupe bg-white"}`}>
        {on && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
        )}
      </span>
    </button>
  );
}

/** The texted link's send (D-237.71) — ONE lock (a second fire is a second text, Class A). The outcome is honest (s168):
 *  "Sent" only on sent:true; otherwise the failure message and the link to use directly. */
type PhoneResult = { sent: boolean; reason: DeliveryReason | "no_people"; link?: string };
function useSendToPhone(clientId: string) {
  const flight = useSingleFlight();
  const mutation = useMutation({
    mutationFn: async (personIds: string[]) =>
      (await apiRequest("POST", `/api/clients/${clientId}/contacts-page/send-to-phone`, { personIds })).json() as Promise<PhoneResult>,
  });
  const [result, setResult] = useState<PhoneResult | null>(null);
  const send = (personIds: string[]) => flight.run(() => mutation.mutateAsync(personIds)
    .then((r) => setResult(r), () => setResult({ sent: false, reason: "failed" })));
  return { send, result, pending: mutation.isPending, reset: () => setResult(null) };
}

/** What a send that did not happen says, and the link to use directly when one was made. */
function SendFailure({ result, testId }: { result: PhoneResult; testId: string }) {
  if (result.sent) return null;
  return (
    <div className="flex flex-col gap-1 text-[13.5px] text-warm-charcoal" role="status" data-testid={testId}>
      <span>{deliveryFailureMessage(result.reason === "no_people" ? "failed" : result.reason, "sms")}</span>
      {result.link && (
        <span className="flex min-w-0 items-center gap-1">
          <span className="min-w-0 break-all text-warm-muted" data-testid={`${testId}-link`}>{result.link}</span>
          <CopyButton text={result.link} title={C.copy.title} testId={`${testId}-copy`} />
        </span>
      )}
    </div>
  );
}

/** Board 12's Download: ONE button with a menu of three (D-237.68-5/6, D-237.71) — the spreadsheet and the contacts file are
 *  made here from the page's own entries; the phone choice texts a link. The first line says who it takes (D-237.72). The
 *  menu is the app's popover (its own layer, Esc and a click away close it), drawn under the button's right edge. */
function DownloadMenu({ list, ticked, onPhone, onOpen }: { list: ContactEntry[]; ticked: boolean; onPhone: () => void; onOpen: () => void }) {
  const [open, setOpen] = useState(false);
  const D = C.download;
  const pick = (i: number) => {
    setOpen(false);
    const files = list.map(toExport);
    if (i === 0) saveTextFile(contactsCsv(files), CONTACTS_FILE_NAMES.csv, "text/csv;charset=utf-8");
    else if (i === 1) saveTextFile(contactsVcf(files), CONTACTS_FILE_NAMES.vcf, "text/vcard;charset=utf-8");
    else onPhone();
  };
  return (
    <Popover open={open} onOpenChange={(o) => { if (o) onOpen(); setOpen(o); }}>
      <PopoverTrigger asChild>
        <button type="button" aria-haspopup="menu"
          className={`flex min-h-12 shrink-0 items-center gap-2 whitespace-nowrap rounded-[12px] border-[1.5px] border-sage-600 px-3.5 text-[14px] text-sage-800 ${open ? "bg-sage-50" : "bg-warm-white"}`}
          data-testid="contacts-download">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
          <span>{D.label}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={open ? "rotate-180" : ""}><path d="m6 9 6 6 6-6" /></svg>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} role="menu"
        className="flex w-[340px] max-w-[calc(100vw-32px)] flex-col rounded-[12px] border border-warm-stone bg-warm-white p-1.5 shadow-[0_6px_20px_rgba(43,42,38,0.12)]"
        data-testid="contacts-download-menu">
        <span className="px-3 pb-1 pt-2 text-[13.5px] text-warm-muted" data-testid="contacts-download-scope">{fill(ticked ? D.ticked : D.shown, { n: list.length })}</span>
        {D.items.map((item, i) => (
          <button key={item.label} type="button" role="menuitem" onClick={() => pick(i)} disabled={list.length === 0}
            className="flex min-h-12 flex-col gap-0.5 rounded-[8px] px-3 py-2.5 text-left hover:bg-sage-50 disabled:opacity-50" data-testid={`contacts-download-${i}`}>
            <span className="text-[15px] text-warm-charcoal">{item.label}</span>
            <span className="text-[13.5px] text-warm-muted">{item.note}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** The card's "Text to my phone" (D-237.71): this ONE person, the same texted link; "Sent — check your texts" once sent. */
function TextToPhone({ clientId, personId }: { clientId: string; personId: string }) {
  const phone = useSendToPhone(clientId);
  return (
    <div className="flex flex-col items-end gap-1">
      <button type="button" onClick={() => void phone.send([personId])}
        className="flex h-11 items-center gap-2 whitespace-nowrap rounded-[8px] border-[1.5px] border-sage-600 bg-warm-white px-4 text-[14px] text-sage-800"
        data-testid="contacts-text-to-phone">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>
        <span>{phone.result?.sent ? C.toPhone.sent : C.toPhone.label}</span>
      </button>
      {phone.result && !phone.result.sent && <SendFailure result={phone.result} testId="contacts-text-to-phone-failed" />}
    </div>
  );
}

/** A company's person to ask for (board 12, item 146(1)) — read from the rows that name the company, shown, not edited here. */
function AskForBox({ names }: { names: readonly string[] }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid="contacts-card-ask-for">
      <span className="text-[13px] text-warm-muted">{C.card.askFor}</span>
      <div className={`flex min-h-11 items-center rounded-[8px] border border-warm-stone bg-white px-3 text-[14px] ${names.length ? "text-warm-charcoal" : "text-warm-muted"}`}>
        {names.length ? names.join(", ") : C.card.nobodyNamed}
      </div>
    </div>
  );
}

/** The card's keys, given ids so it draws their boxes (the card skips a key it has no id for). They name this page's
 *  person fields, never a template field: the card's Done hands the changes back to `contactPatch`. */
const CARD_FIELD_IDS: ReadonlyMap<string, string> = new Map([["first", "contact-first"], ["last", "contact-last"], ["name", "contact-name"],
  ["phone", "contact-phone"], ["email", "contact-email"], ["address", "contact-address"]]);

function Icon({ company }: { company: boolean }) {
  return company ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-warm-muted" data-testid="contacts-icon-company">
      <path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M3 21h18M8 8h3M8 12h3M8 16h3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-warm-muted" data-testid="contacts-icon-person">
      <circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

/** A role: a link to the built screen that chose it, or plain words while that screen is not built (D-237.37). */
function Role({ role, onOpen }: { role: ContactEntry["roles"][number]; onOpen: (screenKey: string) => void }) {
  return role.target ? (
    <button type="button" onClick={() => onOpen(role.target!.screen)}
      className="flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-[8px] px-1.5 text-[13.5px] text-sage-800 underline decoration-warm-stone underline-offset-[3px]"
      data-testid="contacts-role-link">{role.words}</button>
  ) : (
    <span className="flex min-h-11 shrink-0 items-center whitespace-nowrap px-1.5 text-[13.5px] text-warm-charcoal" data-testid="contacts-role-words">{role.words}</span>
  );
}

/**
 * The roles column, ONE line (rule 133(a), board 12): two roles, then "+N more"; and when even those do not fit the
 * column, the expand control. Either opens every role under the row.
 */
function RolesCell({ roles, open, onToggle, onOpen }: {
  roles: ContactEntry["roles"]; open: boolean; onToggle: () => void; onOpen: (screenKey: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setClipped(el.scrollWidth - el.clientWidth > 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [roles]);
  const shown = roles.slice(0, 2);
  const more = roles.length > 2;
  return (
    <div className="flex min-w-0 items-center" data-testid="contacts-roles">
      <div ref={ref} className="flex min-w-0 flex-nowrap items-center gap-x-1 overflow-hidden">
        {shown.map((r, i) => <Role key={i} role={r} onOpen={onOpen} />)}
      </div>
      {(more || clipped) && (
        <button type="button" onClick={onToggle} aria-expanded={open} title={HOME_BOARD.where.showAll}
          className="flex min-h-11 shrink-0 items-center whitespace-nowrap px-1.5 text-[13.5px] text-sage-800" data-testid="contacts-roles-more">
          {open ? C.roles.fewer : more ? fill(C.roles.more, { n: roles.length - 2 }) : "…"}
        </button>
      )}
    </div>
  );
}
